import { Injectable } from '@angular/core';
import { ScrollPanelComponent } from '../../Basic/components/ScrollPanel/scroll-panel.component';

@Injectable()
export class FocusHelper {
    /**
     * Finds the first FormMessage in the given element and focuses the related input.
     * If scrollPanel is provided scrolls to the focused input.
     * If no related input can be found focuses the element with aria-describedby attribute that matches the error id.
     * If no element with matching aria-describedby attribute can be found and scrollPanel is provided, scrolls directly to the FormMessage.
     * In order for this to work properly the FormMessages themselves or their container must have a unique id
     * that is provided to their related input via aria-describedby attribute.
     * Multiple FormMessages for the same element should be contained in an ul.
     * @param element The HTMLElement that contains the errors
     * @param errorClass Query selector for error messages, default is .agpr-form-message
     * @param scrollPanel The scrollPanel component that contains the errors
     */
    public static focusFirstError(element: Element, errorClass: string = '.agpr-form-message', scrollPanel?: ScrollPanelComponent): void {
        requestAnimationFrame(() => {
            let firstError: HTMLElement = element.querySelector(errorClass);
            if (firstError) {
                if (firstError.id === 'null' || firstError.id === '') {
                // When there are multiple errors for a single element they will be contained in a ul with the id linking to the element
                    firstError = firstError.closest('ul');
                }
                const inputTags = 'INPUT, BUTTON, [role=button], TEXTAREA, [tabindex="0"]';
                let elementToFocus: HTMLElement = document.querySelector(`[aria-describedby*=${firstError?.id}]`);
                if (elementToFocus) {
                // Check if elementToFocus is focusable, if not then find the first child that is
                    if (!inputTags.includes(elementToFocus.tagName)) {
                        const firstFocusableChild: HTMLElement = elementToFocus.querySelector(inputTags);
                        if (firstFocusableChild) {
                            elementToFocus = firstFocusableChild;
                        } else {
                        // elementToFocus is not focusable AND has no focusable children. This is the worst case scenario.
                        // We will focus the FormMessage instead.
                            firstError.setAttribute('tabindex', '0');
                            elementToFocus = firstError;
                        }
                    }
                    elementToFocus.focus();
                    if (scrollPanel) {
                        scrollPanel.scrollFirstIntoViewNative([elementToFocus]);
                    }
                } else { // No related element, focus the FormMessage itself.
                    firstError?.setAttribute('tabindex', '0');
                    firstError?.focus();
                    if (scrollPanel) {
                        scrollPanel.scrollFirstIntoViewNative([firstError]);
                    }
                }
            }
        });
    }

    /**
     * Focuses the next available chip when a chip is removed.
     * @param chipList The container that holds the chips
     * @param index Index of the chip that was removed
     * @param chipSelector Unique selector that identifies the element in each chip that should receive focus
     */
    public static handleFocusAfterChipRemoval(chipList: Element, index: number, chipSelector: string): void {
        requestAnimationFrame(() => {
            const chips = chipList.querySelectorAll(chipSelector);
            if (index === chips.length) {
                index--;
            }
            if (chips[index]) {
                (chips[index] as HTMLElement).focus();
            }
        });
    }

    public static focusError(errorId: string) {
        requestAnimationFrame(() => {
            const elementToFocus: HTMLElement = document.querySelector(`[aria-describedby*=${errorId}]`);
            const tagName = 'UL';
            if (elementToFocus?.tagName === tagName) {
                const blockButtonToFocus: HTMLElement = elementToFocus?.querySelector('button:first-child');
                blockButtonToFocus?.focus();
            } else {
                elementToFocus?.focus();
            }
        });
    }
}
